// Shared number + currency formatters for the dashboard.
//
// One place so hero numbers, trend deltas, and tile labels render
// consistently. Built as pure functions with no external dependencies
// so the Node-pool test suite can exercise them without pulling in a
// DOM.
//
// Principles:
//   - Abbreviate large numbers ($6.42M, $182K) for the hero slot so
//     the tile stays compact.
//   - Never lose precision on percentages in the source — format at
//     the edge only.
//   - Deltas always carry an explicit sign glyph (+ / −) so the
//     direction is unambiguous before color lands.
//
// "Minus" uses the Unicode minus (U+2212, "−") not an ASCII hyphen.
// Typographically correct and matches the spec language in
// grant-dashboard-v3.md ("↓ −$4K vs last week").

export interface FormatDollarOptions {
  // If true, render in thousands/millions abbreviated form ($6.42M).
  // If false, render full dollar value ($1,234,567). Default: true.
  abbreviated?: boolean;
  // Number of decimals to show in the abbreviated form. Default: 2
  // for millions, 1 for thousands, 0 for below thousand.
  decimals?: number;
}

export function formatDollars(
  value: number | null | undefined,
  options: FormatDollarOptions = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }
  const { abbreviated = true } = options;
  if (!abbreviated) {
    return `$${Math.round(value).toLocaleString("en-US")}`;
  }

  const abs = Math.abs(value);
  const sign = value < 0 ? "−" : "";

  if (abs >= 1_000_000) {
    const decimals = options.decimals ?? 2;
    return `${sign}$${(abs / 1_000_000).toFixed(decimals)}M`;
  }
  if (abs >= 1_000) {
    const decimals = options.decimals ?? 1;
    // Drop the trailing .0 on round thousands ($48K vs $48.0K)
    const raw = (abs / 1_000).toFixed(decimals);
    const trimmed = raw.endsWith(".0") ? raw.slice(0, -2) : raw;
    return `${sign}$${trimmed}K`;
  }
  return `${sign}$${Math.round(abs)}`;
}

// Abbreviated delta with explicit sign glyph. Used for week-over-week
// pills and tile trend lines. Never returns "—" — caller should decide
// whether to render at all.
export function formatDeltaDollars(value: number): string {
  if (value === 0) {
    return "$0";
  }
  const sign = value > 0 ? "+" : "−";
  const abs = Math.abs(value);
  return `${sign}${formatDollars(abs).replace("−", "")}`;
}

// Percent with one decimal place, optional +/−. Used for trend indicators
// on Pulse tiles and trend pills.
export function formatPercent(
  value: number | null | undefined,
  options: { decimals?: number; withSign?: boolean } = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }
  const { decimals = 1, withSign = false } = options;
  const rounded = value.toFixed(decimals);
  if (!withSign) {
    return `${rounded}%`;
  }
  if (value > 0) return `+${rounded}%`;
  if (value < 0) return `−${Math.abs(parseFloat(rounded)).toFixed(decimals)}%`;
  return `${rounded}%`;
}

// Percentage points (pp) delta — e.g. "+1.4pp" for "cross-sell rate up
// 1.4 percentage points vs last quarter". Distinct from formatPercent
// because rate-of-a-rate confusion is real.
export function formatPercentagePointDelta(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }
  const rounded = Math.abs(value).toFixed(1);
  if (value > 0) return `+${rounded}pp`;
  if (value < 0) return `−${rounded}pp`;
  return `${rounded}pp`;
}

// Plain integer with thousands separators. Used for counts (households,
// active deals). Handles null gracefully so tiles with missing counts
// show em-dash rather than "undefined".
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }
  return Math.round(value).toLocaleString("en-US");
}

// Integer delta — e.g. "+62" for "62 new households this quarter".
// Matches the dashboard spec language ("+62 net new Q2").
export function formatCountDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }
  const abs = Math.abs(Math.round(value));
  if (value > 0) return `+${abs.toLocaleString("en-US")}`;
  if (value < 0) return `−${abs.toLocaleString("en-US")}`;
  return "0";
}

// Relative-time label for staleness indicators on QuickBooks tiles
// and Client-engine last-update meta. Short form ("6h ago", "2d ago",
// "just now") so it fits on a 220px-wide tile footer.
export function formatRelativeTime(
  timestamp: string | null | undefined,
  nowMs: number = Date.now(),
): string {
  if (!timestamp) return "—";
  const then = Date.parse(timestamp);
  if (Number.isNaN(then)) return "—";

  const deltaMs = nowMs - then;
  if (deltaMs < 0) return "just now";
  const minutes = Math.round(deltaMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;

  const weeks = Math.round(days / 7);
  if (weeks < 8) return `${weeks}w ago`;

  const months = Math.round(days / 30);
  return `${months}mo ago`;
}
